import { Button } from '@/components/Button'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import { useId, type ReactNode } from 'react'
import type { BookkeepingConfirmationAccessDialogViewModel } from './BookkeepingConfirmationAccessDialogViewModel'
import { DEFAULT, match } from '@/utils/pattern-match-util'
import { TextField } from '@/components/inputs/TextField'
import { InputShellSlot } from '@/components/inputs/InputShell'
import { Form, FormField, FormFieldLabel } from '@/components/Form'
import {
  formatBookkeepingPlatform,
  getBookkeepingPlatformLoginUrl,
} from '@/features/bookkeeping/platform-connections/bookkeeping-platform-connection-formatters'

interface BookkeepingConfirmationAccessDialogContainerProps {
  vm: BookkeepingConfirmationAccessDialogViewModel
}

/**
 * The BookkeepingConfirmationAccessDialogContainer
 */
export const BookkeepingConfirmationAccessDialogContainer = observer(
  ({ vm }: BookkeepingConfirmationAccessDialogContainerProps) => {
    return (
      <BookkeepingConfirmationAccessDialog
        isOpen={vm.modal.showing}
        onClose={vm.modal.close}
        loginUrl={getBookkeepingPlatformLoginUrl(vm.bookkeepingPlatform)}
        title={vm.connectionName}
        isPending={vm.confirmAccess.pending}
        description={match(vm.bookkeepingPlatform, {
          QuickBooks: <QuickBooksDescription />,
          Xero: <XeroDescription />,
          [DEFAULT]: <div>Not supported</div>,
        })}
        openPlatformLabel={`Open ${formatBookkeepingPlatform(vm.bookkeepingPlatform)}`}
        onProvideAccess={vm.confirmDialogVm.show}
        testId="confirm-access-dialog"
        accessProvidedTestId="access-provided-button"
      >
        <Form>
          <CopyTextField
            label="First name"
            value="Bookkeeping"
            onCopy={() => vm.copyToClipboard('Bookkeeping')}
          />
          <CopyTextField
            label="Last name"
            value="Taxfyle"
            onCopy={() => vm.copyToClipboard('Taxfyle')}
          />

          <CopyTextField
            label="Email address"
            value="bookkeeping@taxfyle.com"
            onCopy={() => vm.copyToClipboard('bookkeeping@taxfyle.com')}
            testId="copy-email-button"
          />
        </Form>

        <ChooseBookkeepingPlatformButton
          onClick={vm.chooseBookkeepingPlatform}
          isDisabled={vm.confirmAccess.pending}
        />
        <ConfirmDialogContainer vm={vm} />
      </BookkeepingConfirmationAccessDialog>
    )
  },
)

interface ConfirmAccessDialogContainerProps {
  vm: BookkeepingConfirmationAccessDialogViewModel
}

/**
 * The ConfirmAccessDialogContainer
 */
export const ConfirmDialogContainer = observer(
  ({ vm }: ConfirmAccessDialogContainerProps) => {
    return (
      <ConfirmAccessDialog
        platformName={formatBookkeepingPlatform(vm.bookkeepingPlatform)}
        connectionName={vm.connectionName}
        isOpen={vm.confirmDialogVm.showing}
        onClose={vm.confirmDialogVm.close}
        isPending={vm.confirmAccess.pending}
        onConfirm={vm.confirmAccess}
        testId="confirm-access-confirmation-dialog"
        confirmTestId="yes-confirmed-access-button"
      />
    )
  },
)

interface BookkeepingConfirmationAccessDialogProps {
  /**
   * Content to display in the dialog.
   */
  children?: ReactNode
  /**
   * Title of the dialog.
   */
  title: string
  /**
   * Description content for the dialog.
   */
  description: ReactNode
  /**
   * Label for the open platform button.
   */
  openPlatformLabel: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether an operation is pending.
   */
  isPending?: boolean
  /**
   * Whether the dialog is disabled.
   */
  isDisabled?: boolean
  /**
   * Login URL for the platform.
   */
  loginUrl: string | null
  /**
   * Called when the dialog is closed.
   */
  onClose?: () => unknown
  /**
   * Called when access is confirmed as provided.
   */
  onProvideAccess?: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the access provided button.
   */
  accessProvidedTestId?: string
}

/**
 * The BookkeepingConfirmationAccessDialog
 */
export function BookkeepingConfirmationAccessDialog({
  children,
  title,
  description,
  openPlatformLabel,
  isOpen,
  isPending,
  loginUrl,
  onClose,
  onProvideAccess,
  testId,
  accessProvidedTestId,
}: BookkeepingConfirmationAccessDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          Add Taxfyle Bookkeeping to your {title}
        </ModalDialogTitle>
        <ModalDialogDescription>{description}</ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Access provided"
            isPending={isPending}
            onPress={onProvideAccess}
            testId={accessProvidedTestId}
          />
          {loginUrl && (
            <Button
              label={openPlatformLabel}
              href={loginUrl}
              variant="outline"
              target="_blank"
              isPending={isPending}
            />
          )}
          <Button
            label="Cancel"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

/**
 * The QuickBooksDescription
 */
export function QuickBooksDescription() {
  return (
    <>
      <p>
        Go to <span className="text-strong">Manage users</span> in QuickBooks.
        Under the <span className="text-strong">Accounting firms</span> tab,
        click <span className="text-strong">Invite</span>. Use the information
        below to complete the necessary fields to add us to your books.
      </p>
      <br />
      <p>
        Once granted, click <span className="text-strong">Access Provided</span>{' '}
        to let us know.
      </p>
    </>
  )
}

/**
 * The XeroDescription
 */
export function XeroDescription() {
  return (
    <>
      <p>
        Click on your organisation name in Xero and select{' '}
        <span className="text-strong">Settings</span>, then click{' '}
        <span className="text-strong">Users</span>. Click{' '}
        <span className="text-strong">Invite a user</span> and use the
        information below to complete the necessary fields to add us to your
        books.
      </p>
      <br />
      <p>
        Once granted, click <span className="text-strong">Access Provided</span>{' '}
        below to let us know.
      </p>
    </>
  )
}

interface CopyTextFieldProps {
  label: string
  value: string
  onCopy: () => unknown
  testId?: string
}

/**
 * The CopyTextField
 */
export function CopyTextField({
  label,
  value,
  onCopy,
  testId,
}: CopyTextFieldProps) {
  const id = useId()
  return (
    <FormField>
      <FormFieldLabel for={id}>{label}</FormFieldLabel>
      <TextField id={id} value={value} readOnly>
        <InputShellSlot side="right">
          <Button
            label="Copy"
            variant="text"
            onPress={onCopy}
            testId={testId}
          />
        </InputShellSlot>
      </TextField>
    </FormField>
  )
}

interface ChooseBookkeepingPlatformButtonProps {
  isDisabled: boolean
  onClick: () => unknown
}

/**
 * The ChooseBookkeepingPlatformButton
 */
export function ChooseBookkeepingPlatformButton({
  isDisabled,
  onClick,
}: ChooseBookkeepingPlatformButtonProps) {
  return (
    <div className="mt-lg">
      <Button
        label="Connect a different account"
        variant="text-inline"
        isDisabled={isDisabled}
        onPress={onClick}
      />
    </div>
  )
}

interface ConfirmAccessDialogProps {
  /**
   * Name of the connection.
   */
  connectionName: string
  /**
   * Name of the platform.
   */
  platformName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether the confirm operation is pending.
   */
  isPending: boolean
  /**
   * Called when the dialog is closed.
   */
  onClose: () => unknown
  /**
   * Called when access is confirmed.
   */
  onConfirm: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm button.
   */
  confirmTestId?: string
}

/**
 * The ConfirmAccessDialog
 */
export function ConfirmAccessDialog({
  platformName,
  connectionName,
  isOpen,
  isPending,
  onClose,
  onConfirm,
  testId,
  confirmTestId,
}: ConfirmAccessDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Confirm access to {platformName}</ModalDialogTitle>
        <ModalDialogDescription>
          Please verify that{' '}
          <span className="text-strong">{connectionName}</span> granted{' '}
          {platformName} access to{' '}
          <span className="text-strong"> Taxfyle Bookkeeping.</span>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, I confirmed access"
            isPending={isPending}
            onPress={onConfirm}
            testId={confirmTestId}
          />
          <Button
            label="Back"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
